import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  LogOut, Download, Phone, Mail, MessageCircle, Trash2, Eye, Filter,
} from 'lucide-react';
import {
  fetchLeads, updateLead, deleteLead, exportLeadsCSV, clearToken, getToken,
} from '../../api';
import { toast } from 'sonner';

const statusLabels = {
  pending: 'Pendente',
  contacted: 'Contactada',
  closed: 'Fechada',
};

const statusColors = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  contacted: 'bg-blue-100 text-blue-800 border-blue-200',
  closed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const whatsappLink = (phone) => {
  const clean = String(phone || '').replace(/\D/g, '');
  return `https://wa.me/${clean}`;
};

export const AdminDashboard = ({ onLogout }) => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, contacted: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchLeads(statusFilter, typeFilter);
      setLeads(data.leads || []);
      setStats(data.stats || { total: 0, pending: 0, contacted: 0, closed: 0 });
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Sessão expirada');
        onLogout();
      } else {
        toast.error('Erro ao carregar leads');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter]);

  const handleStatusChange = async (lead, newStatus) => {
    try {
      await updateLead(lead.id, { status: newStatus });
      toast.success('Estado atualizado');
      load();
    } catch {
      toast.error('Erro ao atualizar');
    }
  };

  const handleDelete = async (lead) => {
    if (!window.confirm(`Eliminar lead de ${lead.name}?`)) return;
    try {
      await deleteLead(lead.id);
      toast.success('Lead eliminada');
      load();
    } catch {
      toast.error('Erro ao eliminar');
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedLead) return;
    try {
      await updateLead(selectedLead.id, { notes: notesDraft });
      toast.success('Notas guardadas');
      setSelectedLead({ ...selectedLead, notes: notesDraft });
      load();
    } catch {
      toast.error('Erro ao guardar notas');
    }
  };

  const openLead = (lead) => {
    setSelectedLead(lead);
    setNotesDraft(lead.notes || '');
  };

  const handleExport = () => {
    const token = getToken();
    // Build URL with token in query for direct download
    const url = exportLeadsCSV();
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'leads_rustem.csv';
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error('Erro ao exportar'));
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-30">
        <div className="container mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-stone-500 font-medium">Painel</p>
            <h1 className="text-xl font-light text-stone-900">
              Rustem<span className="text-stone-400">.</span> Leads
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleExport}
              data-testid="admin-export-button"
              className="rounded-none border-stone-300 text-stone-700 hover:bg-stone-100 text-sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
            <Button
              variant="ghost"
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="rounded-none text-stone-500 hover:text-stone-900 text-sm"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 lg:px-12 py-12">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12" data-testid="admin-stats">
          {[
            { label: 'Total', value: stats.total },
            { label: 'Pendentes', value: stats.pending },
            { label: 'Contactadas', value: stats.contacted },
            { label: 'Fechadas', value: stats.closed },
          ].map((s) => (
            <div key={s.label} className="border-t border-stone-300 pt-6">
              <div className="text-xs tracking-wider uppercase text-stone-500 font-medium">{s.label}</div>
              <div className="text-4xl font-light text-stone-900 mt-2">{s.value}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
          <div className="flex items-center gap-2 text-stone-600">
            <Filter className="w-4 h-4" />
            <span className="text-xs tracking-wider uppercase font-medium">Filtros</span>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48 rounded-none border-stone-300" data-testid="filter-status">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os estados</SelectItem>
              <SelectItem value="pending">Pendente</SelectItem>
              <SelectItem value="contacted">Contactada</SelectItem>
              <SelectItem value="closed">Fechada</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-48 rounded-none border-stone-300" data-testid="filter-type">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              <SelectItem value="simple">Contacto rápido</SelectItem>
              <SelectItem value="detailed">Briefing detalhado</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Leads list */}
        {loading ? (
          <div className="text-center py-20 text-stone-500">A carregar...</div>
        ) : leads.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-stone-300">
            <p className="text-stone-500">Sem leads para estes filtros.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="leads-list">
            {leads.map((lead) => (
              <div
                key={lead.id}
                className="bg-white border border-stone-200 hover:border-stone-400 transition-colors p-6"
                data-testid={`lead-row-${lead.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border ${statusColors[lead.status]}`}>
                        {statusLabels[lead.status]}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider text-stone-500">
                        {lead.type === 'simple' ? 'Contacto rápido' : `Briefing · ${lead.intent === 'buy' ? 'Compra' : 'Venda'}`}
                      </span>
                      <span className="text-[10px] text-stone-400">{formatDate(lead.created_at)}</span>
                    </div>
                    <div className="font-medium text-stone-900">{lead.name}</div>
                    <div className="flex items-center gap-4 text-xs text-stone-500 mt-1 flex-wrap">
                      <a href={`tel:${lead.phone}`} className="flex items-center gap-1 hover:text-stone-900">
                        <Phone className="w-3 h-3" />{lead.phone}
                      </a>
                      <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-stone-900">
                        <Mail className="w-3 h-3" />{lead.email}
                      </a>
                      {lead.location && (
                        <span className="text-stone-500">· {lead.location}</span>
                      )}
                      {lead.budget && (
                        <span className="text-stone-500">· {lead.budget}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Select
                      value={lead.status}
                      onValueChange={(v) => handleStatusChange(lead, v)}
                    >
                      <SelectTrigger className="w-36 rounded-none border-stone-300 text-xs" data-testid={`status-select-${lead.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pendente</SelectItem>
                        <SelectItem value="contacted">Contactada</SelectItem>
                        <SelectItem value="closed">Fechada</SelectItem>
                      </SelectContent>
                    </Select>
                    <a
                      href={whatsappLink(lead.phone)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-stone-500 hover:text-[#25D366] transition-colors"
                      title="Abrir WhatsApp"
                      data-testid={`wa-${lead.id}`}
                    >
                      <MessageCircle className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => openLead(lead)}
                      className="p-2 text-stone-500 hover:text-stone-900 transition-colors"
                      title="Ver detalhes"
                      data-testid={`view-${lead.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(lead)}
                      className="p-2 text-stone-500 hover:text-red-600 transition-colors"
                      title="Eliminar"
                      data-testid={`delete-${lead.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Lead detail dialog */}
      <Dialog open={!!selectedLead} onOpenChange={(o) => !o && setSelectedLead(null)}>
        <DialogContent className="max-w-2xl rounded-none" data-testid="lead-detail-dialog">
          {selectedLead && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-light">{selectedLead.name}</DialogTitle>
                <DialogDescription className="text-stone-500">
                  {selectedLead.type === 'simple' ? 'Contacto rápido' : `Briefing · ${selectedLead.intent === 'buy' ? 'Compra' : 'Venda'}`} · {formatDate(selectedLead.created_at)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Telefone" value={selectedLead.phone} />
                  <Field label="Email" value={selectedLead.email} />
                </div>
                {selectedLead.type === 'detailed' && (
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Tipo de imóvel" value={selectedLead.propertyType} />
                    <Field label="Quartos" value={selectedLead.bedrooms ? `T${selectedLead.bedrooms}` : ''} />
                    <Field label="Localização" value={selectedLead.location} />
                    <Field label="Orçamento" value={selectedLead.budget} />
                    <Field label="Fator principal" value={selectedLead.mainFactor} className="col-span-2" />
                  </div>
                )}
                {(selectedLead.message || selectedLead.additionalInfo) && (
                  <Field
                    label={selectedLead.type === 'simple' ? 'Mensagem' : 'Informações adicionais'}
                    value={selectedLead.message || selectedLead.additionalInfo}
                    multiline
                  />
                )}

                <div>
                  <div className="text-xs tracking-wider uppercase text-stone-500 font-medium mb-2">
                    Notas internas
                  </div>
                  <Textarea
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    rows={3}
                    placeholder="Adicione observações sobre este contacto..."
                    className="rounded-none border-stone-300"
                    data-testid="notes-textarea"
                  />
                </div>
              </div>

              <DialogFooter className="gap-2">
                <a
                  href={whatsappLink(selectedLead.phone)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white px-4 py-2 text-sm font-medium"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
                <Button onClick={handleSaveNotes} data-testid="save-notes-button" className="rounded-none bg-stone-900 hover:bg-stone-800">
                  Guardar notas
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, value, multiline, className = "" }) => (
  <div className={className}>
    <div className="text-xs tracking-wider uppercase text-stone-500 font-medium">{label}</div>
    <div className={`text-stone-900 mt-1 ${multiline ? 'whitespace-pre-wrap' : ''}`}>
      {value || '—'}
    </div>
  </div>
);
